import React, { useEffect, useMemo, useState } from "react";
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  Grid,
  Stack,
  Paper,
  Chip,
  Divider,
  useMediaQuery,
} from "@mui/material";

// MUI icons
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";

import welcomeImage from "./assets/logo2.png";

// You can tune these to match your brochure brand
const BRAND = {
  blue: "#0D73B9",
  blue2: "#0A5CA0",
  red: "#B70000",
  bg: "#F6F9FC",
  text: "#0B1F33",
  muted: "rgba(11,31,51,0.70)",
  border: "rgba(11,31,51,0.10)",
};

const theme = createTheme({
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"',
  },
  shape: { borderRadius: 16 },
});

function WelcomePage() {
  const [direction, setDirection] = useState("ltr");

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const dir = useMemo(() => direction || "ltr", [direction]);

  useEffect(() => {
    const savedDirection = localStorage.getItem("direction") || "ltr";
    setDirection(savedDirection);
  }, []);

  useEffect(() => {
    document.body.setAttribute("dir", dir);
  }, [dir]);

  const logoUrl = welcomeImage;


  const featureChips = [
    {
      icon: <DashboardRoundedIcon fontSize="small" />,
      label: "Unified Modules",
    },
    {
      icon: <SpeedRoundedIcon fontSize="small" />,
      label: "Maximum Performance",
    },
    {
      icon: <ShieldRoundedIcon fontSize="small" />,
      label: "Stable & Reliable",
    },
    {
      icon: <PhoneIphoneRoundedIcon fontSize="small" />,
      label: "Mobile Control",
    },
  ];

  const modules = [
    {
      icon: <EventAvailableRoundedIcon />,
      title: "Appointments & Call Center",
      desc: "Scheduling, follow-ups, and streamlined intake.",
    },
    {
      icon: <GroupsRoundedIcon />,
      title: "Reception & Patient Management",
      desc: "Admissions, patient profiles, OPD/IPD flow.",
    },
    {
      icon: <MedicationRoundedIcon />,
      title: "Pharmacy",
      desc: "Prescription workflow and stock-aware dispensing.",
    },
    {
      icon: <ScienceRoundedIcon />,
      title: "Laboratory / Diagnostics",
      desc: "Orders, results, and fast reporting.",
    },
    {
      icon: <PaymentsRoundedIcon />,
      title: "Billing & Finance",
      desc: "Invoices, payments, and financial reporting.",
    },
    {
      icon: <ApartmentRoundedIcon />,
      title: "Room / Ward Management",
      desc: "Wards, rooms, beds, and real-time status.",
    },
    {
      icon: <LocalHospitalRoundedIcon />,
      title: "ICU & IPD",
      desc: "Critical care and inpatient management support.",
    },
    {
      icon: <HealthAndSafetyRoundedIcon />,
      title: "Emergency Department",
      desc: "Rapid registration and emergency billing workflow.",
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(180deg, ${BRAND.bg} 0%, #ffffff 70%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background shapes (brochure-like blue curves + red accents) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            "&::before": {
              content: '""',
              position: "absolute",
              width: { xs: 520, md: 820 },
              height: { xs: 520, md: 820 },
              top: { xs: -240, md: -360 },
              left: dir === "rtl" ? "auto" : { xs: -260, md: -340 },
              right: dir === "rtl" ? { xs: -260, md: -340 } : "auto",
              background: `radial-gradient(circle at 30% 30%, ${BRAND.blue} 0%, ${BRAND.blue2} 35%, rgba(13,115,185,0.0) 70%)`,
              filter: "blur(0px)",
              opacity: 0.95,
              borderRadius: "50%",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              width: { xs: 360, md: 520 },
              height: { xs: 360, md: 520 },
              bottom: { xs: -180, md: -240 },
              right: dir === "rtl" ? "auto" : { xs: -170, md: -220 },
              left: dir === "rtl" ? { xs: -170, md: -220 } : "auto",
              background: `radial-gradient(circle at 40% 40%, rgba(183,0,0,0.24) 0%, rgba(183,0,0,0.10) 35%, rgba(183,0,0,0.0) 70%)`,
              borderRadius: "50%",
            },
          }}
        />

        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, sm: 3 },
            py: { xs: 5, md: 7 },
            position: "relative",
          }}
        >
          {/* Top brand bar */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mb: { xs: 4, md: 6 },
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  background: `linear-gradient(135deg, ${BRAND.blue} 0%, ${BRAND.blue2} 100%)`,
                  display: "grid",
                  placeItems: "center",
                  boxShadow: "0 12px 30px rgba(13,115,185,0.25)",
                }}
              >
                <LocalHospitalRoundedIcon sx={{ color: "white" }} />
              </Box>
              <Box>
                <Typography
                  sx={{ fontWeight: 800, color: BRAND.text, lineHeight: 1.1 }}
                >
                  Roghtoon Plus
                </Typography>
                <Typography sx={{ fontSize: 13, color: BRAND.muted }}>
                  Hospital Management System • Web Application v1.0
                </Typography>
              </Box>
            </Stack>

            <Chip
              label="AFGHAN COSMOS"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.6,
                border: `1px solid ${BRAND.border}`,
                backgroundColor: "rgba(255,255,255,0.65)",
                backdropFilter: "blur(10px)",
              }}
            />
          </Stack>

          {/* Hero */}
          <Grid container spacing={3} alignItems="stretch">
            {/* Left: Value proposition */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2.5, sm: 3.5, md: 4 },
                  borderRadius: 4,
                  border: `1px solid ${BRAND.border}`,
                  backgroundColor: "rgba(255,255,255,0.70)",
                  backdropFilter: "blur(12px)",
                  boxShadow: "0 24px 70px rgba(11,31,51,0.08)",
                }}
              >
                <Stack spacing={2.25}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: BRAND.text,
                      fontSize: { xs: 30, sm: 36, md: 44 },
                      lineHeight: 1.05,
                      letterSpacing: -0.6,
                    }}
                  >
                    Organize your Hospital{" "}
                    <Box component="span" sx={{ color: BRAND.red }}>
                      Today
                    </Box>
                  </Typography>

                  <Typography
                    sx={{
                      color: BRAND.muted,
                      fontSize: { xs: 14.5, sm: 16 },
                      lineHeight: 1.8,
                    }}
                  >
                    Roghtoon Plus is a comprehensive hospital management system
                    designed to streamline operational, clinical, and
                    administrative processes—supporting key departments from
                    admissions and outpatient/inpatient care to billing,
                    pharmacy, diagnostics, inventory, and HR.{" "}
                    {/* Based on brochure overview */}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ pt: 0.5 }}
                  >
                    {featureChips.map((c) => (
                      <Chip
                        key={c.label}
                        icon={c.icon}
                        label={c.label}
                        sx={{
                          fontWeight: 700,
                          borderRadius: 999,
                          border: `1px solid ${BRAND.border}`,
                          backgroundColor: "rgba(255,255,255,0.70)",
                        }}
                      />
                    ))}
                  </Stack>

                  <Divider sx={{ borderColor: BRAND.border }} />

                  <Grid container spacing={1.5}>
                    {modules.slice(0, isMdDown ? 6 : 8).map((m) => (
                      <Grid key={m.title} item xs={12} sm={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "100%",
                            borderRadius: 3,
                            border: `1px solid ${BRAND.border}`,
                            background:
                              "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.55) 100%)",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2.5,
                                display: "grid",
                                placeItems: "center",
                                background: `linear-gradient(135deg, rgba(13,115,185,0.14) 0%, rgba(183,0,0,0.10) 100%)`,
                                color: BRAND.blue2,
                                flex: "0 0 auto",
                              }}
                            >
                              {React.cloneElement(m.icon, {
                                fontSize: "small",
                              })}
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 850,
                                  color: BRAND.text,
                                  fontSize: 15.5,
                                  lineHeight: 1.25,
                                }}
                              >
                                {m.title}
                              </Typography>
                              <Typography
                                sx={{
                                  color: BRAND.muted,
                                  fontSize: 13.25,
                                  mt: 0.5,
                                  lineHeight: 1.55,
                                }}
                              >
                                {m.desc}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Box
                    sx={{
                      mt: 0.5,
                      p: 2,
                      borderRadius: 3,
                      border: `1px dashed ${BRAND.border}`,
                      backgroundColor: "rgba(255,255,255,0.55)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <SpeedRoundedIcon sx={{ color: BRAND.blue2 }} />
                      <Typography
                        sx={{
                          color: BRAND.muted,
                          fontSize: 13.5,
                          lineHeight: 1.6,
                        }}
                      >
                        Built for high-speed processing and stability—even
                        during peak hours (OPD rush, emergencies, audits).
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Right: Logo / product visual */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 4,
                  border: `1px solid ${BRAND.border}`,
                  background: `linear-gradient(135deg, rgba(13,115,185,0.10) 0%, rgba(255,255,255,0.75) 55%, rgba(183,0,0,0.08) 100%)`,
                  boxShadow: "0 24px 70px rgba(11,31,51,0.08)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Stack spacing={2} sx={{ flex: 1 }}>
                  <Stack spacing={0.75}>
                    <Typography
                      sx={{ fontWeight: 900, color: BRAND.text, fontSize: 18 }}
                    >
                      Welcome
                    </Typography>
                    <Typography
                      sx={{
                        color: BRAND.muted,
                        fontSize: 13.5,
                        lineHeight: 1.65,
                      }}
                    >
                      A unified system for hospitals and clinics—built to
                      simplify daily workflows across departments.
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: isMobile ? 220 : 320,
                      borderRadius: 3,
                      border: `1px solid ${BRAND.border}`,
                      backgroundColor: "rgba(255,255,255,0.70)",
                      display: "grid",
                      placeItems: "center",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "radial-gradient(circle at 25% 20%, rgba(13,115,185,0.18) 0%, rgba(13,115,185,0.0) 55%), radial-gradient(circle at 80% 70%, rgba(183,0,0,0.12) 0%, rgba(183,0,0,0.0) 55%)",
                      }}
                    />
                    <img
                      src={logoUrl}
                      alt="Roghtoon Plus Hospital Logo"
                      style={{
                        width: "78%",
                        maxWidth: 360,
                        height: "auto",
                        position: "relative",
                        filter: "drop-shadow(0 18px 28px rgba(11,31,51,0.12))",
                      }}
                    />
                  </Box>

                  <Stack spacing={1}>
                    <Typography
                      sx={{
                        fontWeight: 850,
                        color: BRAND.text,
                        fontSize: 14.5,
                      }}
                    >
                      Quick Highlights
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        icon={<PhoneIphoneRoundedIcon fontSize="small" />}
                        label="Control via Mobile"
                        sx={{
                          border: `1px solid ${BRAND.border}`,
                          backgroundColor: "rgba(255,255,255,0.65)",
                          fontWeight: 700,
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<DashboardRoundedIcon fontSize="small" />}
                        label="Dashboards & Reports"
                        sx={{
                          border: `1px solid ${BRAND.border}`,
                          backgroundColor: "rgba(255,255,255,0.65)",
                          fontWeight: 700,
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<ApartmentRoundedIcon fontSize="small" />}
                        label="Rooms & Beds"
                        sx={{
                          border: `1px solid ${BRAND.border}`,
                          backgroundColor: "rgba(255,255,255,0.65)",
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2, borderColor: BRAND.border }} />

                <Typography sx={{ color: BRAND.muted, fontSize: 12.5 }}>
                  Powered by Afghan Cosmos • Designed for real hospital
                  workflows.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default WelcomePage;
